import { useMemo } from "react";
import { format, addDays, startOfDay, isSameDay, getDay, getISOWeek } from "date-fns";
import { sv } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface DateStripProps {
  selectedDate: Date | null;
  onSelect: (date: Date | null) => void;
  organizationId?: string;
  showAllButton?: boolean;
  days?: number;
}

type DateItem = {
  type: "date";
  date: Date;
  label: string;
  isToday: boolean;
  isTomorrow: boolean;
  isWeekend: boolean;
  key: string;
};

type WeekLabelItem = {
  type: "weekLabel";
  weekNumber: number;
  key: string;
};

type StripItem = DateItem | WeekLabelItem;

export default function DateStrip({
  selectedDate,
  onSelect,
  organizationId,
  showAllButton = true,
  days = 30,
}: DateStripProps) {
  const { t, i18n } = useTranslation();
  const today = useMemo(() => startOfDay(new Date()), []);
  const weekPrefix = i18n.language === "en" ? "W" : "v.";

  const items = useMemo(() => {
    const result: StripItem[] = [];
    let lastWeek: number | null = null;

    for (let i = 0; i < days; i++) {
      const d = addDays(today, i);
      const dayOfWeek = getDay(d);
      const weekNumber = getISOWeek(d);

      if (weekNumber !== lastWeek) {
        result.push({
          type: "weekLabel",
          weekNumber,
          key: `week-${weekNumber}`,
        });
        lastWeek = weekNumber;
      }

      result.push({
        type: "date",
        date: d,
        label:
          i === 0
            ? t("today")
            : i === 1
              ? t("tomorrow")
              : format(d, "EEE", { locale: sv }),
        isToday: i === 0,
        isTomorrow: i === 1,
        isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
        key: format(d, "yyyy-MM-dd"),
      });
    }
    return result;
  }, [today, days, t]);

  // Activity dot indicator: fetch dates that have open activities
  const endDate = useMemo(() => addDays(today, days), [today, days]);

  const { data: activityDates } = useQuery({
    queryKey: ["activity-dates-30d", organizationId, today.toISOString()],
    queryFn: async () => {
      let query = supabase
        .from("activities")
        .select("start_time")
        .gte("start_time", today.toISOString())
        .lte("start_time", endDate.toISOString())
        .eq("status", "open");

      if (organizationId) {
        query = query.eq("organization_id", organizationId);
      }

      const { data } = await query;
      if (!data) return new Set<string>();

      const dateSet = new Set<string>();
      for (const row of data) {
        dateSet.add(format(new Date(row.start_time), "yyyy-MM-dd"));
      }
      return dateSet;
    },
    enabled: true,
    staleTime: 60_000,
  });

  return (
    <div className="flex gap-1 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 items-stretch">
      {/* "Alla" button */}
      {showAllButton && (
        <button
          onClick={() => onSelect(null)}
          className={cn(
            "flex-shrink-0 flex flex-col items-center justify-center px-3 py-2 rounded-lg text-xs min-w-[52px] transition-colors",
            selectedDate === null
              ? "bg-primary text-primary-foreground"
              : "bg-muted hover:bg-muted/80"
          )}
        >
          <span className="font-medium">{t("all")}</span>
        </button>
      )}

      {items.map((item) => {
        if (item.type === "weekLabel") {
          return (
            <div
              key={item.key}
              className="flex-shrink-0 flex items-center justify-center px-1"
            >
              <span className="text-[10px] text-muted-foreground font-medium whitespace-nowrap">
                {weekPrefix}{item.weekNumber}
              </span>
            </div>
          );
        }

        const { date, label, isWeekend, key } = item;
        const isSelected = selectedDate !== null && isSameDay(selectedDate, date);
        const hasActivities = activityDates?.has(key) ?? false;

        return (
          <button
            key={key}
            onClick={() => onSelect(date)}
            className={cn(
              "flex-shrink-0 flex flex-col items-center px-3 py-2 rounded-lg text-xs min-w-[52px] transition-colors",
              isSelected
                ? "bg-primary text-primary-foreground"
                : isWeekend
                  ? "bg-orange-50 hover:bg-orange-100 dark:bg-orange-950/30 dark:hover:bg-orange-950/50"
                  : "bg-muted hover:bg-muted/80"
            )}
          >
            <span
              className={cn(
                "text-[10px]",
                isSelected ? "text-primary-foreground/80" : "text-muted-foreground"
              )}
            >
              {label}
            </span>
            <span className="font-bold text-base leading-tight">
              {format(date, "d")}
            </span>
            <span
              className={cn(
                "text-[10px]",
                isSelected ? "text-primary-foreground/80" : "text-muted-foreground"
              )}
            >
              {format(date, "MMM", { locale: sv })}
            </span>
            {hasActivities && (
              <div
                className={cn(
                  "w-1.5 h-1.5 rounded-full mt-0.5",
                  isSelected ? "bg-primary-foreground" : "bg-primary"
                )}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
